import {create} from "zustand";
import type {AppState} from "./data/state/AppState";
import type {View} from "./data/models/View";
import {XyiconFeature} from "./generated/api/base";
import type {ScopeWideSearchType} from "./ui/5.0/topbar/OrganizationWideSearchUtils";
import type {SettingsModuleSubNav} from "./Enums";

export type NavBarState = "closed" | "open" | "docked" | "hidden";

export interface IViewNotificationStatus {
	[XyiconFeature.Portfolio]: boolean;
	[XyiconFeature.XyiconCatalog]: boolean;
	[XyiconFeature.Space]: boolean;
}

type TStoreState = {
	appState: AppState;
	navBarState: NavBarState;
	isRootFrozen: boolean;
	viewForOpenSpaceSelector: View | null; // when we open the spaceselector, we open the selected space with this view as the selected one for spaceeditor. When it's null, the spaceselector is closed
	modulesTypeAndFieldSearchString: string;
	modulesCreatingType: SettingsModuleSubNav | "";
	viewNotificationStatus: IViewNotificationStatus;
	isScopeWideSearchOpen: boolean;
	scopeWideSearchType: ScopeWideSearchType;
	panelWidths: Record<string, number>;
	isPortfolioSwitchRequestInProgress: boolean;
};

type TStoreAction = {
	setAppState: (newAppState: AppState) => void;
	setNavBarState: (value: NavBarState) => void;
	setIsRootFrozen: (value: boolean) => void;
	setViewForOpenSpaceSelector: (value: View | null) => void;
	setModulesTypeAndFieldSearchString: (value: string) => void;
	setModulesCreatingType: (type: SettingsModuleSubNav | "") => void;
	setViewNotificationStatus: (key: keyof IViewNotificationStatus, value: boolean) => void;
	setIsScopeWideSearchOpen: (value: boolean) => void;
	setScopeWideSearchType: (value: ScopeWideSearchType) => void;
	setPanelWidth: (id: string, width: number) => void;
	setIsPortfolioSwitchRequestInProgress: (value: boolean) => void;
};

export const useAppStore = create<TStoreState & TStoreAction>((set) => ({
	appState: null,
	setAppState(newAppState: AppState) {
		set((state) => ({
			...state,
			appState: newAppState,
		}));
	},
	navBarState: "closed",
	setNavBarState: (value) => set(() => ({navBarState: value})),
	isRootFrozen: false,
	setIsRootFrozen: (value) => set(() => ({isRootFrozen: value})),
	viewForOpenSpaceSelector: null,
	setViewForOpenSpaceSelector: (value) => set(() => ({viewForOpenSpaceSelector: value})),
	modulesTypeAndFieldSearchString: "",
	setModulesTypeAndFieldSearchString: (value) => set(() => ({modulesTypeAndFieldSearchString: value})),
	modulesTypeAndFieldCreating: false,
	modulesCreatingType: "",
	setModulesCreatingType: (value) => set(() => ({modulesCreatingType: value})),
	viewNotificationStatus: {
		[XyiconFeature.Portfolio]: true,
		[XyiconFeature.XyiconCatalog]: true,
		[XyiconFeature.Space]: true,
	},
	setViewNotificationStatus: (key, value) =>
		set((state) => ({
			viewNotificationStatus: {
				...state.viewNotificationStatus,
				[key]: value,
			},
		})),
	isScopeWideSearchOpen: false,
	setIsScopeWideSearchOpen: (value) => set(() => ({isScopeWideSearchOpen: value})),
	scopeWideSearchType: "Portfolio",
	setScopeWideSearchType: (value) => set(() => ({scopeWideSearchType: value})),
	//included `id` if not it will change all the panels when multiple panels are open
	panelWidths: {} as Record<string, number>,
	setPanelWidth: (id: string, width: number) =>
		set((state) => ({
			panelWidths: {...state.panelWidths, [id]: width},
		})),
	isPortfolioSwitchRequestInProgress: false,
	setIsPortfolioSwitchRequestInProgress: (value) => set(() => ({isPortfolioSwitchRequestInProgress: value})),
}));
